import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUsersStore } from '@/stores/users'
import Cookie from '@/services/cookie'
import { shouldBlockAccess } from '@/utils/accessTime'

const CHECK_INTERVAL_MS = 30 * 1000 // 30 segundos

/**
 * Composable que monitora continuamente se o horário do usuário expirou
 * enquanto a aplicação permanece aberta (ex: chat ou dashboard).
 *
 * Previne memory leak limpando os timers no onUnmounted e
 * revalida instantaneamente ao alternar de aba (visibilitychange).
 */
export function useAccessTimeMonitor() {
  const router = useRouter()
  const usersStore = useUsersStore()

  let timerId = null

  const isExpiredRoute = () => {
    const currentRoute = router.currentRoute.value
    return currentRoute.name === 'expired-access' || currentRoute.path === '/acesso-expirado'
  }

  const check = () => {
    // Se a rota atual já é a de acesso expirado, não faz nada
    if (isExpiredRoute()) return

    // Se não há token, o middleware de auth é quem trata
    const token = Cookie.getAuthToken()
    if (!token) return

    const user = usersStore.me
    if (!user || !user.id) return

    if (shouldBlockAccess(user)) {
      console.warn('[AccessTimeMonitor] Horário de acesso expirado. Redirecionando para /acesso-expirado')
      router.push({ name: 'expired-access' })
    }
  }

  const onVisibilityChange = () => {
    if (!document.hidden) {
      // Ao voltar para a aba, verifica imediatamente caso o timer tenha sido suspenso pelo navegador
      check()
    }
  }

  const start = () => {
    if (timerId) return
    check()
    timerId = setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  const stop = () => {
    if (timerId) {
      clearInterval(timerId)
      timerId = null
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  onMounted(() => {
    start()
  })

  onUnmounted(() => {
    stop()
  })

  return {
    check,
    start,
    stop,
  }
}

export default useAccessTimeMonitor
